import React, { useState, useEffect, useMemo } from 'react';
import {
  Save,
  Truck,
  Globe,
  Bell,
  Shield,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';

const TABS = [
  { id: 'shipping', label: 'Kargo', icon: Truck },
  { id: 'site', label: 'Site', icon: Globe },
  { id: 'notifications', label: 'Bildirimler', icon: Bell },
  { id: 'security', label: 'Güvenlik', icon: Shield }
];

const emptySettings = {
  site: {
    name: 'LeaserTech',
    description: '',
    currency: 'TRY',
    language: 'tr'
  },
  security: {
    sessionTimeout: 24,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
    requireEmailVerification: true,
    twoFactorAuth: false
  },
  notifications: {
    newOrderEmail: true,
    lowStockAlert: true,
    userRegistrationEmail: true,
    orderStatusEmail: true
  },
  shipping: {
    freeShippingThreshold: 300,
    defaultShippingCost: 50,
    expressShippingCost: 249,
    estimatedDeliveryDays: 3
  }
};

const mergeDeep = (base, patch) => {
  const out = { ...base };
  Object.keys(patch || {}).forEach((key) => {
    const val = patch[key];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      out[key] = mergeDeep(base[key] || {}, val);
    } else {
      out[key] = val;
    }
  });
  return out;
};

const parseNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const AdminSettings = () => {
  const { updateSettings: syncGlobalSettings, meta: globalMeta, loadSettings: reloadGlobal } = useSettings();
  const [activeTab, setActiveTab] = useState('shipping');
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  const loadSettings = async () => {
    try {
      setInitialLoading(true);
      const response = await settingsAPI.getSettings();
      const payload = response.data;
      const next = mergeDeep(emptySettings, payload.settings ?? payload);
      setSettings(next);
      setLastSavedAt(payload.meta?.updatedAt || null);
      setDirty(false);
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
      toast.error('Ayarlar sunucudan yüklenemedi');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleInputChange = (section, field, value) => {
    setDirty(true);
    setSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.updateSettings(settings);
      const saved = response.data.settings ?? settings;
      const meta = response.data.meta ?? { updatedAt: new Date().toISOString() };
      setSettings(mergeDeep(emptySettings, saved));
      setLastSavedAt(meta.updatedAt);
      setDirty(false);
      syncGlobalSettings(saved, meta);
      window.dispatchEvent(new Event('leasertech-settings-updated'));
      toast.success('Ayarlar kaydedildi — site genelinde geçerli');
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.msg ||
        'Ayarlar kaydedilemedi';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const shippingPreview = useMemo(() => {
    const s = settings.shipping;
    return {
      threshold: parseNumber(s.freeShippingThreshold, 300),
      standard: parseNumber(s.defaultShippingCost, 50),
      express: parseNumber(s.expressShippingCost, 249),
      days: parseNumber(s.estimatedDeliveryDays, 3)
    };
  }, [settings.shipping]);

  if (initialLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-24">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mağaza ayarları</h1>
          <p className="mt-1 text-sm text-slate-600">
            Kargo ücretleri ve site bilgileri checkout, sepet ve sipariş hesaplamasında anında kullanılır.
          </p>
          {(lastSavedAt || globalMeta?.updatedAt) && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Son kayıt:{' '}
              {new Date(lastSavedAt || globalMeta.updatedAt).toLocaleString('tr-TR')}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            loadSettings();
            reloadGlobal({ silent: true });
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Sunucudan yenile
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 rounded-t-lg px-4 py-2.5 text-sm font-medium transition ${
                active
                  ? 'bg-white text-orange-600 shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {activeTab === 'shipping' && (
          <div className="p-6 space-y-6">
            <div className="rounded-lg bg-orange-50 border border-orange-100 p-4 text-sm text-orange-900">
              <strong>Canlı önizleme:</strong> Sepet / ödeme ekranında standart kargo{' '}
              <strong>₺{shippingPreview.standard}</strong>, ücretsiz kargo eşiği{' '}
              <strong>₺{shippingPreview.threshold}</strong> ve üzeri; hızlı teslimat{' '}
              <strong>₺{shippingPreview.express}</strong>.
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Ücretsiz kargo eşiği (₺)"
                hint="Sepet tutarı bu değere ulaşınca standart kargo ücretsiz."
                type="number"
                min={0}
                value={settings.shipping.freeShippingThreshold}
                onChange={(e) =>
                  handleInputChange('shipping', 'freeShippingThreshold', parseNumber(e.target.value, 0))
                }
              />
              <Field
                label="Standart kargo ücreti (₺)"
                hint="Yurtiçi kargo — eşik altındaki siparişler."
                type="number"
                min={0}
                value={settings.shipping.defaultShippingCost}
                onChange={(e) =>
                  handleInputChange('shipping', 'defaultShippingCost', parseNumber(e.target.value, 0))
                }
              />
              <Field
                label="Hızlı teslimat ücreti (₺)"
                hint="İstanbul içi express seçeneği."
                type="number"
                min={0}
                value={settings.shipping.expressShippingCost}
                onChange={(e) =>
                  handleInputChange('shipping', 'expressShippingCost', parseNumber(e.target.value, 0))
                }
              />
              <Field
                label="Tahmini teslimat (gün)"
                type="number"
                min={1}
                max={30}
                value={settings.shipping.estimatedDeliveryDays}
                onChange={(e) =>
                  handleInputChange('shipping', 'estimatedDeliveryDays', parseNumber(e.target.value, 3))
                }
              />
            </div>
          </div>
        )}

        {activeTab === 'site' && (
          <div className="p-6 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Site adı"
                value={settings.site.name}
                onChange={(e) => handleInputChange('site', 'name', e.target.value)}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Para birimi</label>
                <select
                  value={settings.site.currency}
                  onChange={(e) => handleInputChange('site', 'currency', e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
                >
                  <option value="TRY">TRY (₺)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Site açıklaması</label>
              <textarea
                rows={3}
                value={settings.site.description}
                onChange={(e) => handleInputChange('site', 'description', e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="divide-y divide-slate-100 p-2">
            <ToggleRow
              label="Yeni sipariş e-postası (admin)"
              checked={settings.notifications.newOrderEmail}
              onChange={(v) => handleInputChange('notifications', 'newOrderEmail', v)}
            />
            <ToggleRow
              label="Düşük stok uyarısı"
              checked={settings.notifications.lowStockAlert}
              onChange={(v) => handleInputChange('notifications', 'lowStockAlert', v)}
            />
            <ToggleRow
              label="Kullanıcı kayıt bildirimi"
              checked={settings.notifications.userRegistrationEmail}
              onChange={(v) => handleInputChange('notifications', 'userRegistrationEmail', v)}
            />
            <ToggleRow
              label="Sipariş durumu e-postası (müşteri)"
              description="Kargoda, iptal vb. durumlarda müşteriye mail gider."
              checked={settings.notifications.orderStatusEmail}
              onChange={(v) => handleInputChange('notifications', 'orderStatusEmail', v)}
            />
          </div>
        )}

        {activeTab === 'security' && (
          <div className="p-6 space-y-5">
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Info className="h-5 w-5 shrink-0" />
              <p>
                Oturum süresi ve giriş denemesi limitleri kayıt altına alınır; tam uygulama için backend
                entegrasyonu aşamalı genişletilebilir. E-posta doğrulama kayıt akışında aktiftir.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Oturum süresi (saat)"
                type="number"
                min={1}
                max={168}
                value={settings.security.sessionTimeout}
                onChange={(e) =>
                  handleInputChange('security', 'sessionTimeout', parseNumber(e.target.value, 24))
                }
              />
              <Field
                label="Maks. giriş denemesi"
                type="number"
                min={3}
                max={10}
                value={settings.security.maxLoginAttempts}
                onChange={(e) =>
                  handleInputChange('security', 'maxLoginAttempts', parseNumber(e.target.value, 5))
                }
              />
            </div>
            <ToggleRow
              label="E-posta doğrulama zorunlu"
              checked={settings.security.requireEmailVerification}
              onChange={(v) => handleInputChange('security', 'requireEmailVerification', v)}
            />
            <ToggleRow
              label="İki faktörlü kimlik doğrulama"
              checked={settings.security.twoFactorAuth}
              onChange={(v) => handleInputChange('security', 'twoFactorAuth', v)}
            />
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur md:left-64">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <span className="text-sm text-slate-500">
            {dirty ? 'Kaydedilmemiş değişiklikler var' : 'Tüm değişiklikler kayıtlı'}
          </span>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-orange-600 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {loading ? 'Kaydediliyor…' : 'Kaydet ve yayınla'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, hint, ...inputProps }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    <input
      {...inputProps}
      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
    />
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

const ToggleRow = ({ label, description, checked, onChange }) => (
  <label className="flex cursor-pointer items-start gap-3 px-4 py-4 hover:bg-slate-50">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
    />
    <span>
      <span className="block text-sm font-medium text-slate-800">{label}</span>
      {description && <span className="mt-0.5 block text-xs text-slate-500">{description}</span>}
    </span>
  </label>
);

export default AdminSettings;
